import { format, parseISO } from "date-fns";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import {
  Chip,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "notistack";
import EditIcon from "@mui/icons-material/Edit";

import {
  fetchVendors,
  clearStatus,
  setSelectedVendors,
  sendLienWaiverNotices,
  downloadVendorInsuranceReport,
  downloadTPFoldersReport,
  clearInsuranceReportStatus,
  clearTPFoldersReportStatus,
} from "../../../redux/slices/vendorsSlice";
import { apiGet } from "../../../services/api/apiClient";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

import Table from "../../../components/Table";
import VendorsTableControls from "./components/TableControls";
import UpdateShippingAddressDialog from "./components/UpdateShippingAddressDialog";
import { exportVendorsToXLSX } from "../../../utils/xlsxExport";
import { PageHeader } from "../../../ui";

const LienWaivers = () => {
  const dispatch = useAppDispatch();
  const {
    vendors,
    status,
    filters,
    selectedVendors,
    sendStatus,
    sendError,
    sendResult,
    syncStatus,
    syncError,
    syncResult,
    vendorsTotal,
    insuranceReportStatus,
    insuranceReportError,
    tpFoldersReportStatus,
    tpFoldersReportError,
  } = useAppSelector((state) => state.vendors) as any;
  const { enqueueSnackbar } = useSnackbar();

  // Pagination + sorting state (0-based page, matches the table)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Bumped after send/sync mutations so the current page is refetched.
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  // Column search (debounced) — sent to the server as per-column filters.
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const debouncedColumnSearch = useDebouncedValue(columnSearch, 400);

  // Unfiltered total, used for the "Export All" menu label. Fetched lazily
  // (only when the export menu opens) so page load does not fire a second
  // request. Cached for the session via a ref guard.
  const [allVendorsTotal, setAllVendorsTotal] = useState<number | null>(null);
  const allVendorsTotalFetchedRef = useRef(false);

  const fetchAllVendorsTotal = useCallback(async () => {
    if (allVendorsTotalFetchedRef.current) return;
    allVendorsTotalFetchedRef.current = true;
    try {
      const data = await apiGet({
        endpoint: "/lien-waiver",
        params: { page: "1", pageSize: "1" },
        withAuth: true,
      });
      setAllVendorsTotal(typeof data?.total === "number" ? data.total : 0);
    } catch {
      setAllVendorsTotal(0);
    }
  }, []);

  // Dialog state
  const [shippingAddressDialogOpen, setShippingAddressDialogOpen] =
    useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<any>(null);

  // Expand all state
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState<any>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Whenever the filters/search change we refetch from page 0. filterKey
  // tracks only the query inputs (not page/sort), so changing a filter resets
  // the page exactly once and triggers a single refetch.
  const filterKey = [
    filters.invoicePaymentStatus?.join(",") || "",
    filters.emailSent?.join(",") || "",
    filters.customerAddress || "",
    filters.customerName || "",
    debouncedColumnSearch.vendorName || "",
    debouncedColumnSearch.vendorEmail || "",
    debouncedColumnSearch.invoiceNumber || "",
    debouncedColumnSearch.customerNames || "",
  ].join("|");
  const [queryKey, setQueryKey] = useState(filterKey);
  const prevFilterKey = useRef(filterKey);

  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setPage(0);
      setQueryKey(filterKey);
    }
  }, [filterKey]);

  useEffect(() => {
    dispatch(
      fetchVendors({
        page,
        pageSize: rowsPerPage,
        emailSent: filters.emailSent,
        invoicePaymentStatus: filters.invoicePaymentStatus,
        customerAddress: filters.customerAddress || undefined,
        customerName:
          debouncedColumnSearch.customerNames || filters.customerName || undefined,
        vendorName: debouncedColumnSearch.vendorName || undefined,
        vendorEmail: debouncedColumnSearch.vendorEmail || undefined,
        invoiceNumber: debouncedColumnSearch.invoiceNumber || undefined,
        sortBy: sortBy || undefined,
        sortOrder,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    queryKey,
    refreshNonce,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearStatus());
    };
  }, [dispatch]);

  // Notification effects for send operations
  useEffect(() => {
    if (sendStatus === "succeeded" && sendResult) {
      const { successCount, errorCount, message } = sendResult;

      if (errorCount > 0) {
        // Show warning if there were some errors
        enqueueSnackbar(
          `Processed ${successCount + errorCount} notices: ${successCount} successful, ${errorCount} failed`,
          { variant: "warning" }
        );
      } else {
        // Show success if all were successful
        enqueueSnackbar(
          message || `Successfully sent ${successCount} lien waiver notice(s)`,
          {
            variant: "success",
          }
        );
      }
      // Email statuses changed on the server; refetch the current page.
      setRefreshNonce((n) => n + 1);
    }
  }, [sendStatus, sendResult, enqueueSnackbar]);

  // Clear selection after successful email sending and ensure no sent vendors remain selected
  useEffect(() => {
    if (sendStatus === "succeeded") {
      // Redux already clears selectedVendors, but we double-check to ensure
      // no vendors with isEmailSent: true remain in selection
      const currentlySelectedIds = selectedVendors.map((v: any) => v.id);
      const validSelection = vendors.filter(
        (vendor: any) =>
          currentlySelectedIds.includes(vendor.id) && !vendor.isEmailSent
      );

      // If any selected vendors now have emails sent, update the selection
      if (validSelection.length !== selectedVendors.length) {
        dispatch(setSelectedVendors(validSelection));
      }
    }
  }, [sendStatus, selectedVendors, vendors, dispatch]);

  useEffect(() => {
    if (sendStatus === "failed" && sendError) {
      enqueueSnackbar(`Failed to send lien waiver notices: ${sendError}`, {
        variant: "error",
      });
    }
  }, [sendStatus, sendError, enqueueSnackbar]);

  // Notification effects for sync operations
  useEffect(() => {
    if (syncStatus === "succeeded" && syncResult) {
      const { summary } = syncResult;
      enqueueSnackbar(summary || "Sync completed successfully", {
        variant: "success",
      });
      // Bills/payments changed on the server; refetch the current page.
      setRefreshNonce((n) => n + 1);
    }
  }, [syncStatus, syncResult, enqueueSnackbar]);

  useEffect(() => {
    if (syncStatus === "failed" && syncError) {
      enqueueSnackbar(`Failed to sync lien waivers: ${syncError}`, {
        variant: "error",
      });
    }
  }, [syncStatus, syncError, enqueueSnackbar]);

  // Notification effects for insurance report download
  useEffect(() => {
    if (insuranceReportStatus === "succeeded") {
      enqueueSnackbar("Vendor insurance report downloaded successfully", {
        variant: "success",
      });
      dispatch(clearInsuranceReportStatus());
    }
  }, [insuranceReportStatus, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (insuranceReportStatus === "failed" && insuranceReportError) {
      enqueueSnackbar(
        `Failed to download insurance report: ${insuranceReportError}`,
        {
          variant: "error",
        }
      );
      dispatch(clearInsuranceReportStatus());
    }
  }, [insuranceReportStatus, insuranceReportError, enqueueSnackbar, dispatch]);

  // Notification effects for TP folders report download
  useEffect(() => {
    if (tpFoldersReportStatus === "succeeded") {
      enqueueSnackbar("TP folders report downloaded successfully", {
        variant: "success",
      });
      dispatch(clearTPFoldersReportStatus());
    }
  }, [tpFoldersReportStatus, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (tpFoldersReportStatus === "failed" && tpFoldersReportError) {
      enqueueSnackbar(
        `Failed to download TP folders report: ${tpFoldersReportError}`,
        {
          variant: "error",
        }
      );
      dispatch(clearTPFoldersReportStatus());
    }
  }, [tpFoldersReportStatus, tpFoldersReportError, enqueueSnackbar, dispatch]);

  // Function to handle row selection
  const handleRowSelection = (selectedIds: any) => {
    // Find the selected vendor objects from the current page's vendors array
    const selectedVendorData = vendors.filter((vendor: any) =>
      selectedIds.includes(vendor.id)
    );
    dispatch(setSelectedVendors(selectedVendorData));
  };

  // Function to handle sending lien waiver notices
  const handleSendLienWaiverNotices = () => {
    if (selectedVendors.length > 0) {
      dispatch(sendLienWaiverNotices(selectedVendors));
    }
  };

  // Function to handle downloading vendor insurance report
  const handleDownloadInsuranceReport = () => {
    dispatch(downloadVendorInsuranceReport());
  };

  // Function to handle downloading TP folders report
  const handleDownloadTPFoldersReport = () => {
    dispatch(downloadTPFoldersReport());
  };

  // Function to handle updating shipping address for a line item
  const handleUpdateShippingAddress = useCallback(
    (lineItemId: any, vendorId: any) => {
      const vendor = vendors.find((v: any) => v.id === vendorId);
      if (vendor) {
        const lineItem = vendor.lineItems?.find(
          (item: any) => item.id === lineItemId
        );
        if (lineItem) {
          setSelectedLineItem({ ...lineItem, vendorId });
          setShippingAddressDialogOpen(true);
        }
      }
    },
    [vendors]
  );

  // Function to handle closing the shipping address dialog
  const handleCloseShippingAddressDialog = () => {
    setShippingAddressDialogOpen(false);
    setSelectedLineItem(null);
  };

  // Memoized function to check if selection should be disabled for a row
  const disableSelectionIf = useCallback(
    (row: any) =>
      // Disable if email has already been sent
      row.isEmailSent ||
      // Disable if no line items
      !row.lineItems ||
      row.lineItems.length === 0 ||
      // Disable if any line item has no customer address
      row.lineItems.some(
        (item: any) => !item.customerAddress || item.customerAddress.trim() === ""
      ),
    []
  );

  // Function to format currency amounts with comma separators
  const formatCurrency = useCallback((value: any) => {
    if (value === null || value === undefined || value === "") return "$0";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "$0";
    return `$${numValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  // Function to render expandable content (line items)
  const getExpandableContent = useCallback(
    (vendor: any) => {
      if (!vendor.lineItems || vendor.lineItems.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            No line items available
          </Typography>
        );
      }

      return (
        <Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {vendor.lineItems.map((lineItem: any) => (
              <Box
                key={lineItem.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  backgroundColor: "background.paper",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleUpdateShippingAddress(lineItem.id, vendor.id)
                  }
                  disabled={status === "loading"}
                  title="Update shipping address"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", minWidth: "120px" }}
                    >
                      {lineItem.customerName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {lineItem.customerAddress ||
                        "No address provided. Please, edit."}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: "bold" }}
                  >
                    Amount: {formatCurrency(lineItem.amount)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    },
    [handleUpdateShippingAddress, status, formatCurrency]
  );

  // Function to format payment status as colored chips
  const formatPaymentStatus = useCallback(({ value }: any) => {
    const statusConfig = {
      0: { label: "Paid in Full", color: "success" },
      1: { label: "Open", color: "error" },
      2: { label: "Partial Payment", color: "warning" },
      4: { label: "Scheduled", color: "info" },
      6: { label: "In Process", color: "secondary" },
    };

    const config = (statusConfig as any)[value] || { label: value, color: "default" };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  }, []);

  // Store table headers for export
  const tableHeaders = useMemo(
    () => [
      { key: "vendorName", displayName: "Vendor Name" },
      { key: "vendorEmail", displayName: "Vendor Email" },
      { key: "invoiceNumber", displayName: "Invoice Number" },
      {
        key: "invoiceAmount",
        displayName: "Invoice Amount",
        format: ({ value }: any) => formatCurrency(value),
      },
      {
        key: "invoicePaymentStatus",
        displayName: "Payment Status",
        format: formatPaymentStatus,
      },
      {
        key: "throughDate",
        displayName: "Through Date",
        format: ({ value }: any) => format(parseISO(value), "MM/dd/yy"),
      },
      {
        key: "isEmailSent",
        displayName: "Email Sent",
        format: ({ value }: any) => (value ? "Sent" : "Not Sent"),
      },
      {
        key: "emailSentDate",
        displayName: "Email Sent Date",
        format: ({ value }: any) =>
          value ? format(parseISO(value), "MM/dd/yy") : "-",
      },
      {
        key: "isPMH",
        displayName: "PMH",
        format: ({ value }: any) => (value ? "Yes" : "No"),
      },
      {
        key: "isOnlinePayment",
        displayName: "Online Payment",
        format: ({ value }: any) => (value ? "Yes" : "No"),
      },
      { key: "paymentMethod", displayName: "Payment Method" },
      { key: "departmentName", displayName: "Department" },
    ],
    [formatCurrency, formatPaymentStatus]
  );

  // Handler for opening export menu
  const handleExportClick = useCallback(
    (event: any) => {
      setExportMenuAnchor(event.currentTarget);
      // Fetch the unfiltered total lazily (only when the menu opens) so page
      // load does not fire a second request just for the menu label.
      fetchAllVendorsTotal();
    },
    [fetchAllVendorsTotal]
  );

  // Handler for closing export menu
  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  // Build the server query for an export: all rows (pageSize=all), optionally
  // with the current controls filters applied. The per-column searches are part
  // of what the table shows, so they are part of the filtered export too —
  // otherwise the file holds rows the office filtered out on screen.
  const fetchExportItems = useCallback(
    async (withFilters: boolean) => {
      const params: Record<string, string> = { pageSize: "all" };
      if (withFilters) {
        if (filters.emailSent?.length) {
          params.emailSent = filters.emailSent.join(",");
        }
        if (filters.invoicePaymentStatus?.length) {
          params.invoicePaymentStatus = filters.invoicePaymentStatus.join(",");
        }
        if (filters.customerAddress) {
          params.customerAddress = filters.customerAddress;
        }
        const customerName =
          debouncedColumnSearch.customerNames || filters.customerName;
        if (customerName) {
          params.customerName = customerName;
        }
        if (debouncedColumnSearch.vendorName) {
          params.vendorName = debouncedColumnSearch.vendorName;
        }
        if (debouncedColumnSearch.vendorEmail) {
          params.vendorEmail = debouncedColumnSearch.vendorEmail;
        }
        if (debouncedColumnSearch.invoiceNumber) {
          params.invoiceNumber = debouncedColumnSearch.invoiceNumber;
        }
      }
      const data = await apiGet({
        endpoint: "/lien-waiver",
        params,
        withAuth: true,
      });
      return data?.items || [];
    },
    [filters, debouncedColumnSearch]
  );

  // Handler for exporting filtered vendors to Excel
  const handleExportFiltered = useCallback(async () => {
    handleExportMenuClose();
    try {
      const items = await fetchExportItems(true);
      if (items.length > 0) {
        const timestamp = format(new Date(), "yyyy-MM-dd_HHmm");
        await exportVendorsToXLSX(
          items,
          tableHeaders,
          `lien-waivers-filtered_${timestamp}.xlsx`
        );
        enqueueSnackbar(`Exported ${items.length} filtered vendor(s) to Excel`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar("No filtered data to export", { variant: "warning" });
      }
    } catch (e: any) {
      enqueueSnackbar(`Export failed: ${e.message || "Unknown error"}`, {
        variant: "error",
      });
    }
  }, [fetchExportItems, tableHeaders, enqueueSnackbar, handleExportMenuClose]);

  // Handler for exporting all vendors to Excel
  const handleExportAll = useCallback(async () => {
    handleExportMenuClose();
    try {
      const items = await fetchExportItems(false);
      if (items.length > 0) {
        const timestamp = format(new Date(), "yyyy-MM-dd_HHmm");
        await exportVendorsToXLSX(
          items,
          tableHeaders,
          `lien-waivers-all_${timestamp}.xlsx`
        );
        enqueueSnackbar(`Exported ${items.length} vendor(s) to Excel`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar("No data to export", { variant: "warning" });
      }
    } catch (e: any) {
      enqueueSnackbar(`Export failed: ${e.message || "Unknown error"}`, {
        variant: "error",
      });
    }
  }, [fetchExportItems, tableHeaders, enqueueSnackbar, handleExportMenuClose]);

  // Clean up expanded rows when the visible page changes
  useEffect(() => {
    const currentVendorIds = new Set(vendors.map((v: any) => v.id));
    const cleanedExpandedRows = new Set(
      Array.from(expandedRows).filter((id) => currentVendorIds.has(id))
    );
    if (cleanedExpandedRows.size !== expandedRows.size) {
      setExpandedRows(cleanedExpandedRows);
    }
  }, [vendors, expandedRows]);

  const handleSortChange = useCallback((by: string, ord: "asc" | "desc") => {
    setSortBy(by);
    setSortOrder(ord);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  return (
    <Box
      sx={{
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        right: isFullscreen ? 0 : "auto",
        bottom: isFullscreen ? 0 : "auto",
        zIndex: isFullscreen ? 9999 : "auto",
        backgroundColor: isFullscreen ? "background.default" : "transparent",
        overflow: isFullscreen ? "auto" : "visible",
        p: isFullscreen ? 2 : 0,
      }}
    >
      <PageHeader
        eyebrow="Vendors"
        title="Lien Waivers"
        summary="Who is owed money, which notices went out, and who still needs one."
      />
      <VendorsTableControls
        selectedVendors={selectedVendors}
        sendStatus={sendStatus}
        onSendLienWaiverNotices={handleSendLienWaiverNotices}
        loading={status === "loading"}
        isFullscreen={isFullscreen}
        onToggleFullscreen={handleToggleFullscreen}
        onExport={handleExportClick}
        insuranceReportStatus={insuranceReportStatus}
        onDownloadInsuranceReport={handleDownloadInsuranceReport}
        tpFoldersReportStatus={tpFoldersReportStatus}
        onDownloadTPFoldersReport={handleDownloadTPFoldersReport}
      />
      <Table
        enableColumnSearch
        expandableRows
        getExpandableContent={getExpandableContent}
        getRowCount={(row) => row.lineItems?.length || 0}
        headers={[
          { key: "vendorName", displayName: "Vendor Name", minWidth: "140px" },
          { key: "vendorEmail", displayName: "Vendor Email" },
          {
            key: "customerNames",
            displayName: "Customer Names",
            format: ({ row }: any) => {
              if (!row.lineItems || row.lineItems.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    -
                  </Typography>
                );
              }

              // Get unique customer names
              const uniqueCustomerNames = [
                ...new Set(
                  row.lineItems
                    .map((lineItem: any) => lineItem.customerName)
                    .filter((name: any) => name && name.trim() !== "")
                ),
              ] as string[];

              if (uniqueCustomerNames.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No names
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {uniqueCustomerNames.map((customerName, index) => (
                    <span key={index} style={{ fontSize: 14 }}>
                      {customerName}
                    </span>
                  ))}
                </Box>
              );
            },
            minWidth: "200px",
          },
          { key: "invoiceNumber", displayName: "Invoice Number" },
          {
            key: "invoiceAmount",
            displayName: "Invoice Amount",
            format: ({ value }: any) => formatCurrency(value),
            minWidth: "140px",
            disableColumnSearch: true,
          },
          {
            key: "invoicePaymentStatus",
            displayName: "Payment Status",
            format: formatPaymentStatus,
            minWidth: "140px",
            disableColumnSearch: true,
          },
          {
            key: "throughDate",
            displayName: "Through Date",
            format: ({ value }: any) => format(parseISO(value), "MM/dd/yy"),
            disableColumnSearch: true,
          },
          {
            key: "isEmailSent",
            displayName: "Email Sent",
            format: ({ value }: any) => (
              <Chip
                label={value ? "Sent" : "Not Sent"}
                color={value ? "success" : "default"}
                size="small"
                variant="outlined"
              />
            ),
            disableColumnSearch: true,
          },
          {
            key: "emailSentDate",
            displayName: "Email Sent Date",
            format: ({ value }: any) =>
              value ? format(parseISO(value), "MM/dd/yy") : "-",
            minWidth: "140px",
            disableColumnSearch: true,
          },
          {
            key: "isPMH",
            displayName: "PMH",
            format: ({ value }: any) => (
              <Chip
                label={value ? "Yes" : "No"}
                color={value ? "primary" : "default"}
                size="small"
                variant="outlined"
              />
            ),
            disableColumnSearch: true,
          },
          {
            key: "isOnlinePayment",
            displayName: "Online Payment",
            format: ({ value }: any) => (
              <Chip
                label={value ? "Yes" : "No"}
                color={value ? "success" : "default"}
                size="small"
                variant="outlined"
              />
            ),
            disableColumnSearch: true,
          },
          {
            key: "paymentMethod",
            displayName: "Payment Method",
            minWidth: "140px",
            disableColumnSearch: true,
          },
          {
            key: "departmentName",
            displayName: "Department",
            minWidth: "140px",
            disableColumnSearch: true,
          },
        ]}
        rows={vendors}
        onSelectionChange={handleRowSelection}
        selectionDisabled={
          status === "loading" ||
          sendStatus === "loading" ||
          syncStatus === "loading"
        }
        loading={status === "loading"}
        disableSelectionIf={disableSelectionIf}
        expandedRows={expandedRows}
        onExpandedRowsChange={setExpandedRows}
        serverPagination
        totalCount={vendorsTotal}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onColumnSearchChange={setColumnSearch}
        // Scroll inside the table (below the sticky control bar) instead of
        // with the page, so the sticky header never collides with the bar.
        maxHeight="calc(100vh - 220px)"
      />
      <UpdateShippingAddressDialog
        open={shippingAddressDialogOpen}
        onClose={handleCloseShippingAddressDialog}
        lineItemData={selectedLineItem}
        projectId={selectedLineItem?.vendorId}
      />
      <Menu
        anchorEl={exportMenuAnchor}
        open={exportMenuOpen}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleExportFiltered}>
          Export Filtered ({vendorsTotal || 0} records)
        </MenuItem>
        <MenuItem onClick={handleExportAll}>
          Export All ({allVendorsTotal ?? "..."} records)
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LienWaivers;
